/**
 * ConsoleCommandPalette — the console's ⌘K palette.
 *
 * Built on ``cmdk`` (already a dependency) inside the shared Radix
 * ``DialogContent`` — the SAME portal/overlay machinery as ``ConModal`` —
 * repainted entirely on the ``--con-*`` tokens (the generic ``@ando/ui``
 * ``CommandPalette`` hard-codes the light-only ``ink/cream/orange-soft``
 * classes, which can't theme on graphite). ``useConsoleThemeClass()`` is
 * stamped on the portaled content so the tokens + ``.dark`` flip reach it.
 *
 * Contract:
 *   - SUBSEQUENCE matching (``commandFilter``), not substring.
 *   - Recents shown on an EMPTY query (Linear/Notion behaviour), hidden the
 *     moment the user types.
 *   - Group headings auto-hide when a lane filters to zero (cmdk-native).
 *   - Per-item hairline ``ConKbd`` hint; the keyboard-focused row gets a
 *     subtle ``--con-surface-hover`` cue and an ORANGE icon (never a fill).
 *   - Combobox a11y is cmdk-native: ``role=listbox/option`` +
 *     ``aria-activedescendant`` on the input, ``autoFocus`` on open.
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Command } from "cmdk";
import { CornerDownLeft, type LucideIcon, Search } from "lucide-react";
import * as React from "react";

import { Dialog, DialogContent, cn } from "@ando/ui";

import { commandFilter } from "../lib/commandPalette";
import { useConsoleThemeClass } from "../theme/ConsoleTheme";
import { ConKbd, conFocusRing } from "./atoms";

/** A destination the palette can jump to. */
export interface CommandNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

/** A verb the palette can trigger (open the assistant, mint a key, …). */
export interface CommandActionItem {
  id: string;
  label: string;
  icon: LucideIcon;
  keywords?: string[];
  /** Optional keyboard hint rendered as a hairline ``ConKbd``. */
  kbd?: string;
  run: () => void;
}

export interface ConsoleCommandPaletteProps {
  open: boolean;
  onClose: () => void;
  navItems: readonly CommandNavItem[];
  actions: readonly CommandActionItem[];
  /** Resolved recent destinations (most-recent-first); shown on empty query. */
  recents: readonly CommandNavItem[];
  onNavigate: (to: string) => void;
}

function PaletteRow({
  value,
  icon: Icon,
  label,
  kbd,
  keywords,
  onSelect,
}: {
  value: string;
  icon: LucideIcon;
  label: string;
  kbd?: string;
  keywords?: string[];
  onSelect: () => void;
}): React.ReactElement {
  return (
    <Command.Item value={value} keywords={keywords} onSelect={onSelect}>
      <Icon aria-hidden size={14} strokeWidth={1.75} className="shrink-0" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {kbd ? <ConKbd>{kbd}</ConKbd> : null}
    </Command.Item>
  );
}

export function ConsoleCommandPalette({
  open,
  onClose,
  navItems,
  actions,
  recents,
  onNavigate,
}: ConsoleCommandPaletteProps): React.ReactElement {
  const { i18n } = useLingui();
  const themeClass = useConsoleThemeClass();
  const [query, setQuery] = React.useState("");

  // Fresh query on every open — matches Linear/Notion (reset on the open
  // transition, not on every render, so a programmatic value survives).
  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const label = i18n._(msg`Comandi rapidi`);
  const showRecents = query.trim().length === 0 && recents.length > 0;

  const go = (to: string): void => {
    onNavigate(to);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        ariaTitle={label}
        aria-describedby={undefined}
        className={cn(
          themeClass,
          "con-pop max-w-[560px] gap-0 overflow-hidden p-0",
          "rounded-lg border-[color:var(--con-border)] bg-[color:var(--con-surface-raised)] text-[color:var(--con-on-surface)]",
        )}
      >
        <Command
          label={label}
          filter={commandFilter}
          className={cn(
            "flex flex-col overflow-hidden",
            // Input row.
            "[&_[cmdk-input-wrapper]]:flex [&_[cmdk-input-wrapper]]:items-center [&_[cmdk-input-wrapper]]:gap-2 [&_[cmdk-input-wrapper]]:border-[color:var(--con-border)] [&_[cmdk-input-wrapper]]:border-b [&_[cmdk-input-wrapper]]:px-3",
            "[&_[cmdk-input]]:h-11 [&_[cmdk-input]]:flex-1 [&_[cmdk-input]]:bg-transparent [&_[cmdk-input]]:text-[length:var(--con-text-sm)] [&_[cmdk-input]]:text-[color:var(--con-on-surface)] [&_[cmdk-input]]:outline-none [&_[cmdk-input]]:placeholder:text-[color:var(--con-on-surface-faint)]",
            // List.
            "[&_[cmdk-list]]:max-h-[380px] [&_[cmdk-list]]:overflow-y-auto [&_[cmdk-list]]:overflow-x-hidden [&_[cmdk-list]]:p-1.5",
            // Empty.
            "[&_[cmdk-empty]]:py-8 [&_[cmdk-empty]]:text-center [&_[cmdk-empty]]:text-[length:var(--con-text-xs)] [&_[cmdk-empty]]:text-[color:var(--con-on-surface-faint)]",
            // Group heading — the console's uppercase caption rhythm.
            "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-[length:var(--con-text-2xs)] [&_[cmdk-group-heading]]:text-[color:var(--con-on-surface-faint)] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em]",
            // Item rows — 13px ink, hairline focus cue (NOT a decorative fill).
            "[&_[cmdk-item]]:flex [&_[cmdk-item]]:cursor-pointer [&_[cmdk-item]]:items-center [&_[cmdk-item]]:gap-2.5 [&_[cmdk-item]]:rounded-sm [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2 [&_[cmdk-item]]:text-[length:var(--con-text-sm)] [&_[cmdk-item]]:text-[color:var(--con-on-surface-muted)]",
            "[&_[cmdk-item]_svg]:text-[color:var(--con-on-surface-faint)]",
            // Keyboard-focused row: subtle surface-hover + ink contrast + ORANGE icon.
            "[&_[cmdk-item][data-selected=true]]:bg-[color:var(--con-surface-hover)] [&_[cmdk-item][data-selected=true]]:text-[color:var(--con-on-surface)]",
            "[&_[cmdk-item][data-selected=true]_svg]:text-[color:var(--con-accent-text)]",
          )}
        >
          <div cmdk-input-wrapper="">
            <Search
              aria-hidden
              size={14}
              strokeWidth={1.75}
              className="shrink-0 text-[color:var(--con-on-surface-faint)]"
            />
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder={i18n._(msg`Cerca sezioni e azioni…`)}
              aria-label={i18n._(msg`Cerca sezioni e azioni`)}
            />
            <ConKbd>esc</ConKbd>
          </div>

          <Command.List>
            <Command.Empty>{i18n._(msg`Nessun risultato`)}</Command.Empty>

            {showRecents ? (
              <Command.Group heading={i18n._(msg`Recenti`)}>
                {recents.map((item) => (
                  <PaletteRow
                    key={`recent-${item.to}`}
                    value={`recent ${item.to} ${item.label}`}
                    icon={item.icon}
                    label={item.label}
                    onSelect={() => go(item.to)}
                  />
                ))}
              </Command.Group>
            ) : null}

            <Command.Group heading={i18n._(msg`Vai a`)}>
              {navItems.map((item) => (
                <PaletteRow
                  key={`nav-${item.to}`}
                  value={`${item.label} ${item.to}`}
                  keywords={[item.to]}
                  icon={item.icon}
                  label={item.label}
                  onSelect={() => go(item.to)}
                />
              ))}
            </Command.Group>

            <Command.Group heading={i18n._(msg`Azioni`)}>
              {actions.map((action) => (
                <PaletteRow
                  key={`action-${action.id}`}
                  value={`${action.label} ${action.id}`}
                  keywords={action.keywords}
                  icon={action.icon}
                  label={action.label}
                  kbd={action.kbd}
                  onSelect={() => {
                    action.run();
                    onClose();
                  }}
                />
              ))}
            </Command.Group>
          </Command.List>

          <div className="flex items-center justify-between gap-2 border-[color:var(--con-border)] border-t px-3 py-2 text-[length:var(--con-text-2xs)] text-[color:var(--con-on-surface-faint)]">
            <span className="inline-flex items-center gap-1.5">
              <ConKbd>
                <CornerDownLeft size={11} strokeWidth={1.75} aria-hidden />
              </ConKbd>
              {i18n._(msg`per aprire`)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ConKbd>↑</ConKbd>
              <ConKbd>↓</ConKbd>
              {i18n._(msg`per navigare`)}
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
